<script lang="ts" setup>
import { computed, toRefs } from 'vue'

// Define the props with more explicit type definitions
const props = defineProps<{
  truncateTitle?: boolean
  image: string
  coverImageDominantColors: number[][]
}>()

// Extract the reactive reference for coverImageDominantColors
const { coverImageDominantColors } = toRefs(props)

// Compute newData with improved type checking and concise mapping
const newData = computed(() =>
  coverImageDominantColors.value?.map(innerArray =>
    innerArray.map((num, index) =>
      (num === 251 && index === 1 ? 210 : num),
    ),
  ) || [],
)

// Compute the color string with safe array access
const coverImageDominantColorsComp = computed(() => {
  if (!newData.value.length)
    return '0,0,0'
  const [r, g, b] = newData.value[0] || [0, 0, 0]
  return `${r},${g},${b}`
})
</script>

<template>
  <div
    class="card-root h-49.25 relative overflow-hidden cursor-pointer select-none w-full"
  >
    <div
      :style="`background:linear-gradient(267deg, rgba(${coverImageDominantColorsComp}, 0) 0.39%, rgba(${coverImageDominantColorsComp}, 1) 99.69%)`"
      class="hub-item z-1 flex flex-col justify-between pt-2.5 pl-3 pr-2.5 pb-2 h-full w-full rounded-[15px] min-h-full"
    >
      <div class="flex flex-row">
        <div v-if="$slots.avatar" class="flex flex-row items-start mr-2 gap-3.5">
          <slot name="avatar" />
        </div>
        <div
          class="flex flex-col justify-center "
          :class="{ 'w-full': !$slots.badge, 'truncate': truncateTitle }"
        >
          <div
            class="flex flex-row items-center"
            :class="{ truncate: truncateTitle }"
          >
            <div
              class="text-white font-300 text-5.5 tracking-wide leading-7 capitalize w-full"
              :class="{ 'line-clamp-2': !truncateTitle, 'truncate': truncateTitle }"
              style="word-break: break-word;"
            >
              <slot name="title" />
            </div>

            <slot v-if="$slots.badge" name="badge" />
          </div>
          <div v-if="$slots.subheader" class="text-[#0A84FF] font-500 text-xs">
            <slot name="subheader" />
          </div>
        </div>
      </div>

      <slot />

      <div
        v-if="$slots.bottomLeft || $slots.bottomRight"
        :class="{
          'justify-between': $slots.bottomLeft && $slots.bottomRight,
          'justify-end': !$slots.bottomLeft,
        }"
        class="flex flex-row"
      >
        <div v-if="$slots.bottomLeft" class="flex gap-3">
          <slot name="bottomLeft" />
        </div>
        <div v-if="$slots.bottomRight" class="flex gap-3">
          <slot name="bottomRight" />
        </div>
      </div>
    </div>

    <div
      class="w-full h-full absolute top-0 -z-1  object-cover overflow-hidden"
    >
      <img v-if="image" :src="image" alt="" class="w-full h-full object-cover rounded-2xl">
      <img v-else src="https://belong.net/images/external/other/create-hub-nft-bg.svg" alt="" class="w-full h-full object-cover rounded-2xl">
    </div>
  </div>
</template>

<style scoped>
.card-root {}
</style>
